import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { SlidersHorizontal, X } from "lucide-react";
import { api, useSeo } from "@/lib/api";
import ProductCard from "@/components/store/ProductCard";

const SORTS = [["relevance", "Mais relevantes"], ["bestsellers", "Mais vendidos"], ["price_asc", "Menor preço"], ["price_desc", "Maior preço"], ["new", "Novidades"], ["rating", "Melhor avaliados"]];
const FLAGS = [["customizable", "Personalizável"], ["available", "Disponível"], ["promo", "Em promoção"], ["bestseller", "Mais vendidos"], ["launch", "Lançamentos"]];

export default function Catalog() {
  const [sp, setSp] = useSearchParams();
  const [cats, setCats] = useState([]);
  const [filters, setFilters] = useState({ colors: [], subcategories: [] });
  const [data, setData] = useState({ items: [], total: 0, pages: 1 });
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const get = (k) => sp.get(k) || "";
  const set = (k, v) => { const n = new URLSearchParams(sp); v ? n.set(k, v) : n.delete(k); if (k !== "page") n.delete("page"); setSp(n); };
  const cat = cats.find((c) => c.slug === get("categoria"));
  useSeo({ title: cat ? cat.name : get("q") ? `Busca: ${get("q")}` : "Todos os produtos" });

  useEffect(() => {
    api.get("/categories").then((r) => setCats(r.data));
    api.get("/products/filters").then((r) => setFilters(r.data));
  }, []);
  useEffect(() => {
    setLoading(true);
    const params = { q: get("q"), category: get("categoria"), subcategory: get("sub"), color: get("cor"), stock_type: get("tipo"), sort: get("ordem") || "relevance",
      min_price: get("min") || 0, max_price: get("max") || 0, page: get("page") || 1 };
    FLAGS.forEach(([k]) => { if (get(k)) params[k] = true; });
    api.get("/products", { params }).then((r) => setData(r.data)).finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sp]);

  const Sel = ({ k, label, opts }) => (
    <div><label className="label">{label}</label>
      <select className="input" value={get(k)} onChange={(e) => set(k, e.target.value)} data-testid={`filter-${k}`}>
        <option value="">Todos</option>{opts.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
      </select></div>
  );
  const panel = (
    <div className="space-y-5">
      <Sel k="categoria" label="Categoria" opts={cats.map((c) => [c.slug, c.name])} />
      <Sel k="sub" label="Subcategoria" opts={filters.subcategories.map((s) => [s, s])} />
      <Sel k="cor" label="Cor" opts={filters.colors.map((s) => [s, s])} />
      <Sel k="tipo" label="Tipo" opts={[["pronta_entrega", "Pronta entrega"], ["sob_encomenda", "Sob encomenda"]]} />
      <div><label className="label">Preço</label>
        <div className="flex gap-2"><input className="input" placeholder="Mín" inputMode="numeric" defaultValue={get("min")} onBlur={(e) => set("min", e.target.value)} data-testid="filter-min" /><input className="input" placeholder="Máx" inputMode="numeric" defaultValue={get("max")} onBlur={(e) => set("max", e.target.value)} data-testid="filter-max" /></div></div>
      <div className="space-y-2.5">
        {FLAGS.map(([k, l]) => (
          <label key={k} className="flex items-center gap-3 text-sm cursor-pointer"><input type="checkbox" className="h-4 w-4 accent-[#008CFF]" checked={!!get(k)} onChange={(e) => set(k, e.target.checked ? "1" : "")} data-testid={`filter-${k}`} />{l}</label>
        ))}
      </div>
      <button className="btn-ghost w-full" onClick={() => setSp({})} data-testid="filter-clear">Limpar filtros</button>
    </div>
  );
  const page = Number(get("page") || 1);
  return (
    <div className="max-w-[1440px] mx-auto px-4 lg:px-8 py-10">
      <nav aria-label="breadcrumb" className="text-xs text-[#A0A0A0] mb-4">Início / Produtos{cat && ` / ${cat.name}`}</nav>
      <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
        <div><h1 className="text-3xl lg:text-5xl font-bold">{cat?.name || (get("q") ? `"${get("q")}"` : "Todos os produtos")}</h1><p className="text-sm text-[#A0A0A0] mt-2" data-testid="catalog-total">{data.total} produtos</p></div>
        <div className="flex gap-2 w-full sm:w-auto">
          <button className="btn-ghost lg:hidden flex-1" onClick={() => setOpen(true)} data-testid="open-filters"><SlidersHorizontal className="h-4 w-4" />Filtros</button>
          <select className="input sm:w-56 flex-1" value={get("ordem") || "relevance"} onChange={(e) => set("ordem", e.target.value)} aria-label="Ordenar" data-testid="catalog-sort">
            {SORTS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </div>
      </div>
      <div className="grid lg:grid-cols-[260px_1fr] gap-10">
        <aside className="hidden lg:block">{panel}</aside>
        <div>
          {loading ? <div className="grid grid-cols-2 md:grid-cols-3 gap-4">{[...Array(6)].map((_, k) => <div key={k} className="aspect-[3/4] rounded-2xl bg-[#171A1F] animate-pulse" />)}</div>
            : data.items.length === 0 ? <div className="card p-16 text-center text-[#A0A0A0]" data-testid="catalog-empty">Nenhum produto encontrado com esses filtros.</div>
            : <div className="grid grid-cols-2 md:grid-cols-3 gap-3 sm:gap-4" data-testid="catalog-grid">{data.items.map((p) => <ProductCard key={p.id} p={p} />)}</div>}
          {data.pages > 1 && (
            <div className="flex justify-center gap-2 mt-10">
              {[...Array(data.pages)].map((_, k) => <button key={k} onClick={() => set("page", String(k + 1))} className={`h-10 w-10 rounded-xl border ${page === k + 1 ? "border-[#008CFF] text-[#00C8FF]" : "border-[#282D37]"}`}>{k + 1}</button>)}
            </div>
          )}
        </div>
      </div>
      {open && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="absolute inset-0 bg-black/70" onClick={() => setOpen(false)} />
          <div className="absolute bottom-0 inset-x-0 max-h-[85vh] overflow-y-auto bg-[#0B0D11] border-t border-[#282D37] rounded-t-3xl p-6">
            <div className="flex justify-between items-center mb-6"><h3 className="text-lg font-semibold">Filtros</h3><button onClick={() => setOpen(false)} aria-label="Fechar"><X /></button></div>
            {panel}
            <button className="btn-primary w-full mt-4" onClick={() => setOpen(false)}>Ver {data.total} produtos</button>
          </div>
        </div>
      )}
    </div>
  );
}
